import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../api';
import {
  Plus, Edit2, Trash2, ToggleLeft, ToggleRight,
  Tv, AlertTriangle, X, Check, RefreshCw, CheckCircle, Timer
} from 'lucide-react';

const DEPARTMENTS = [
  'All', 'Pre-flight', 'CMD',
  'Prepress 1', 'Prepress 2', 'Prepress 3',
  'QC',
  'Corrugation', 'Production', 'Dispatch',
];

const MANAGEMENT_ROLES = [
  'admin', 'pm',
  'preflight_lead', 'cmd_lead', 'prepress_lead',
  'qc_lead', 'production_lead', 'epson_lead',
];

const EMPTY_FORM = {
  message: '',
  location: 'All',
  departments: ['All'],
  priority: 'normal',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ── Sub-components ───────────────────────────────────────────────────────────

function PriorityBadge({ priority }) {
  return priority === 'urgent' ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
      <AlertTriangle className="w-3 h-3" /> Urgent
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
      Normal
    </span>
  );
}

function StatusBadge({ active, status }) {
  if (status === 'completed') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200">
      <CheckCircle className="w-3 h-3" /> Completed
    </span>
  );
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
      <Check className="w-3 h-3" /> Active
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
      Inactive
    </span>
  );
}

// ── Message Form Modal ────────────────────────────────────────────────────────

function MessageModal({ initial, locations, onSave, onClose, saving }) {
  // Older messages arrive with only the singular `department`.
  const [form, setForm] = useState(() => {
    const base = initial || EMPTY_FORM;
    return {
      ...base,
      departments: Array.isArray(base.departments) && base.departments.length
        ? base.departments
        : [base.department || 'All'],
    };
  });

  const handleChange = (field, value) => setForm(f => ({ ...f, [field]: value }));

  // 'All' is exclusive: it cannot be combined with specific departments.
  const toggleDept = (d) => setForm(f => {
    const has = f.departments.includes(d);
    let next;
    if (d === 'All') {
      // Unticking 'All' with nothing else chosen would show an empty grid but
      // still save as 'All', so keep it ticked instead of lying to the user.
      next = has ? f.departments : ['All'];
    } else {
      next = has
        ? f.departments.filter(x => x !== d)
        : [...f.departments.filter(x => x !== 'All'), d];
    }
    return { ...f, departments: next };
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.message.trim()) return;
    // Nothing ticked means everyone, matching the backend's default.
    const departments = form.departments.length ? form.departments : ['All'];
    onSave({ ...form, departments, department: departments[0] });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {initial?.id ? 'Edit Alert Message' : 'New Alert Message'}
          </h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Message text */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.message}
              onChange={e => handleChange('message', e.target.value)}
              rows={4}
              maxLength={500}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Type the alert message here..."
              required
            />
            <p className="text-xs text-gray-400 mt-1 text-right">{form.message.length}/500</p>
          </div>

          {/* Location */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <select
              value={form.location}
              onChange={e => handleChange('location', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {locations.map(loc => (
                <option key={loc} value={loc}>{loc}</option>
              ))}
            </select>
          </div>

          {/* Departments — one message can go to several screens at once. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Departments <span className="text-gray-400 font-normal">(tick one or more)</span>
            </label>
            <div className="border border-gray-300 rounded-lg p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {DEPARTMENTS.map(d => {
                const checked = form.departments.includes(d);
                // 'All' already covers every department, so it is exclusive:
                // ticking it clears the rest, and ticking any other clears it.
                const dimmed = d !== 'All' && form.departments.includes('All');
                return (
                  <label
                    key={d}
                    className={`flex items-center gap-2 text-sm cursor-pointer select-none ${dimmed ? 'opacity-40' : 'text-gray-700'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleDept(d)}
                      className="w-4 h-4 accent-blue-600 cursor-pointer"
                    />
                    {d}
                  </label>
                );
              })}
            </div>
            {form.departments.includes('All') && (
              <p className="text-xs text-gray-500 mt-1">Showing on every department screen.</p>
            )}
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio" name="priority" value="normal"
                  checked={form.priority === 'normal'}
                  onChange={() => handleChange('priority', 'normal')}
                  className="accent-blue-600"
                />
                <span className="text-sm text-gray-700">Normal</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio" name="priority" value="urgent"
                  checked={form.priority === 'urgent'}
                  onChange={() => handleChange('priority', 'urgent')}
                  className="accent-red-600"
                />
                <span className="text-sm font-medium text-red-600">⚠ Urgent</span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button" onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !form.message.trim()}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving && <RefreshCw className="w-4 h-4 animate-spin" />}
              {initial?.id ? 'Save Changes' : 'Create Alert'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────

function DeleteConfirmModal({ message, onConfirm, onClose, deleting }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-800">Delete Message?</h2>
            <p className="text-xs text-gray-500">This action cannot be undone.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3 border italic mb-5">
          "{message.message.length > 100 ? message.message.slice(0, 100) + '...' : message.message}"
        </p>
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={deleting}
            className="px-5 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
          >
            {deleting && <RefreshCw className="w-4 h-4 animate-spin" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ImportantMessages() {
  const { user, token } = useAuth();

  const [messages, setMessages] = useState([]);
  const [locations, setLocations] = useState(['All']);
  const [filterLocation, setFilterLocation] = useState('All');
  const [filterDept, setFilterDept] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [completing, setCompleting] = useState(null); // message id being completed

  // Rotation interval setting (seconds), persisted to localStorage
  const [rotationSecs, setRotationSecs] = useState(() => {
    return parseInt(localStorage.getItem('alertMsgRotationSecs') || '120', 10);
  });

  const handleRotationChange = (secs) => {
    setRotationSecs(secs);
    localStorage.setItem('alertMsgRotationSecs', String(secs));
  };

  const canManage = MANAGEMENT_ROLES.includes(user?.role);

  // ── Data fetching ───────────────────────────────────────────────────────────

  const fetchLocations = useCallback(async () => {
    try {
      const res = await api.get('/messages/locations');
      setLocations(res.data || ['All']);
    } catch {
      /* ignore */
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (filterLocation !== 'All') params.location = filterLocation;
      if (filterDept !== 'All') params.department = filterDept;
      const res = await api.get('/messages/all', {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessages(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [filterLocation, filterDept]);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);
  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  // ── Create / Edit ───────────────────────────────────────────────────────────

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const authHeader = { headers: { Authorization: `Bearer ${token}` } };
      if (editTarget?.id) {
        await api.put(`/messages/${editTarget.id}`, form, authHeader);
      } else {
        await api.post('/messages', form, authHeader);
      }
      setShowForm(false);
      setEditTarget(null);
      fetchMessages();
    } catch (e) {
      alert(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (msg) => {
    setEditTarget(msg);
    setShowForm(true);
  };

  const openNew = () => {
    setEditTarget(null);
    setShowForm(true);
  };

  // ── Toggle active ───────────────────────────────────────────────────────────

  const handleToggle = async (msg) => {
    try {
      await api.put(
        `/messages/${msg.id}`,
        { active: !msg.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMessages();
    } catch (e) {
      alert(e.response?.data?.error || 'Toggle failed');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/messages/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteTarget(null);
      fetchMessages();
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ── Complete ────────────────────────────────────────────────────────────────

  const handleComplete = async (msg) => {
    if (completing) return;
    setCompleting(msg.id);
    try {
      await api.put(
        `/messages/${msg.id}`,
        {
          status: 'completed',
          active: false,
          completed_by: user?.name || user?.username || '',
          completed_at: new Date().toISOString(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchMessages();
    } catch (e) {
      alert(e.response?.data?.error || 'Complete action failed');
    } finally {
      setCompleting(null);
    }
  };

  // ── TV Display link ─────────────────────────────────────────────────────────

  const openTVDisplay = () => {
    window.open(`${window.location.origin}/#tv`, '_blank');
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (!canManage) {
    return (
      <div className="p-8 text-center text-gray-500">
        You do not have permission to manage alert messages.
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Page header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Alert Messages</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Manage alert messages displayed on TV screens throughout the facility.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* TV Rotation interval */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
            <Timer className="w-4 h-4 text-gray-500" />
            <span className="text-xs text-gray-600 font-medium whitespace-nowrap">TV Rotation:</span>
            <select
              value={rotationSecs}
              onChange={e => handleRotationChange(Number(e.target.value))}
              className="text-xs border-0 bg-transparent focus:outline-none text-gray-800 font-semibold cursor-pointer"
            >
              <option value={60}>1 min</option>
              <option value={120}>2 min</option>
              <option value={180}>3 min</option>
              <option value={300}>5 min</option>
            </select>
          </div>
          <button
            onClick={openTVDisplay}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 border border-gray-200"
          >
            <Tv className="w-4 h-4" />
            Open TV Display
          </button>
          <button
            onClick={openNew}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            New Alert
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 bg-white border border-gray-200 rounded-xl p-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Location</label>
          <select
            value={filterLocation}
            onChange={e => setFilterLocation(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {locations.map(loc => <option key={loc} value={loc}>{loc}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Department</label>
          <select
            value={filterDept}
            onChange={e => setFilterDept(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-end">
          <button
            onClick={fetchMessages}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {/* Table / content */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-gray-400">
          <RefreshCw className="w-6 h-6 animate-spin mr-2" /> Loading…
        </div>
      ) : error ? (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" /> {error}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <Tv className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No messages found.</p>
          <p className="text-sm mt-1">Click <strong>New Alert</strong> to create one.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Message</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Location</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Department</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Priority</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Created By</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 whitespace-nowrap">Created At</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {messages.map(msg => (
                <tr
                  key={msg.id}
                  className={`hover:bg-gray-50 transition-colors ${
                    msg.status === 'completed'
                      ? 'opacity-60 bg-gray-50'
                      : !msg.active ? 'opacity-50' : ''
                  }`}
                >
                  <td className="px-4 py-3 max-w-xs">
                    {msg.priority === 'urgent' && msg.status !== 'completed' && (
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 inline mr-1.5 -mt-0.5" />
                    )}
                    <span className={`line-clamp-2 ${
                      msg.status === 'completed'
                        ? 'line-through text-gray-400'
                        : 'text-gray-800'
                    }`}>{msg.message}</span>
                    {msg.status === 'completed' && msg.completed_by && (
                      <div className="text-xs text-emerald-600 mt-0.5">✓ Resolved by {msg.completed_by}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{msg.location || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {(() => {
                      const ds = Array.isArray(msg.departments) && msg.departments.length
                        ? msg.departments
                        : [msg.department].filter(Boolean);
                      if (!ds.length) return '—';
                      if (ds.includes('All')) return 'All';
                      return (
                        <div className="flex flex-wrap gap-1">
                          {ds.map(d => (
                            <span key={d} className="px-1.5 py-0.5 rounded bg-gray-100 text-xs whitespace-nowrap">{d}</span>
                          ))}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={msg.priority} /></td>
                  <td className="px-4 py-3"><StatusBadge active={msg.active} status={msg.status} /></td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{msg.created_by || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{formatDate(msg.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      {/* Complete */}
                      {msg.status !== 'completed' && (
                        <button
                          onClick={() => handleComplete(msg)}
                          title="Mark as Completed"
                          disabled={completing === msg.id}
                          className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                        >
                          {completing === msg.id
                            ? <RefreshCw className="w-4 h-4 animate-spin" />
                            : <CheckCircle className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Toggle active (only for non-completed) */}
                      {msg.status !== 'completed' && (
                        <button
                          onClick={() => handleToggle(msg)}
                          title={msg.active ? 'Deactivate' : 'Activate'}
                          className={`p-1.5 rounded-lg transition-colors ${
                            msg.active
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-100'
                          }`}
                        >
                          {msg.active
                            ? <ToggleRight className="w-5 h-5" />
                            : <ToggleLeft className="w-5 h-5" />
                          }
                        </button>
                      )}
                      {/* Edit (only for non-completed) */}
                      {msg.status !== 'completed' && (
                        <button
                          onClick={() => openEdit(msg)}
                          className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                      {/* Delete */}
                      <button
                        onClick={() => setDeleteTarget(msg)}
                        className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <MessageModal
          initial={editTarget}
          locations={locations}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditTarget(null); }}
          saving={saving}
        />
      )}
      {deleteTarget && (
        <DeleteConfirmModal
          message={deleteTarget}
          onConfirm={handleDelete}
          onClose={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}
    </div>
  );
}
